import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {CampaignService} from '../../services/campaign/campaign.service';
import {UserService} from '../../services/user.service';
import {MentionService} from '../../services/mention/mention.service';
import {apiEndpoints} from '../../app.constants';
import {
  CampaignInfluencerStatus,
  CampaignsDetailComponent,
  CampaignTab,
  TermActionType
} from './campaign-detail.component';
import {Location as NgCommonLocation} from '@angular/common';
import {ModalService} from '../../services/modal/modal.service';
import {ReferenceDataService} from '../../services/reference-data/reference-data.service';
import {Campaign} from '../../../models/campaign';
import {ValidationHandlerService} from '../../core/validation-handler.service';
import {WebUtils} from '../../utils/web.utils';
import {
  ActionDetails,
  ActionSource,
  ActivePages,
  EventTrackerService,
  Page,
  TargetType,
  UserActionType
} from '../../services/event-tracker/event-tracker.service';
import {InfluencerGroupsService} from '../influencer-groups/influencer-groups.service';
import {CacheService} from '../../services/cache/cache.service';
import { DateUtils } from '../../utils/date.utils';
import {CampaignInfluencerStatus as CampaignStatus} from '../../app.constants';

@Component({
  selector: 'campaigns-edit',
  templateUrl: './campaign-detail.component.html',
  styleUrls: ['./campaign-detail.component.scss']
})
export class CampaignsEditComponent extends CampaignsDetailComponent implements OnInit {
  public campaignStatus: string;
  public campaignCreatedAt: any;
  public campaignArchivedAt: any;
  public isActiveCampaign: boolean;
  public isArchivedCampaign: boolean;

  private campaign: any;
  private influencers: any[];
  private savedCampaignTerms: any[];

  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    translate: TranslateService,
    campaignService: CampaignService,
    groupService: InfluencerGroupsService,
    userService: UserService,
    mentionService: MentionService,
    modalService: ModalService,
    validateErrorHandler: ValidationHandlerService,
    referenceDataService: ReferenceDataService,
    private location: NgCommonLocation,
    cacheService: CacheService,
    eventTracker: EventTrackerService
  ) {
    super(
      router,
      activatedRoute,
      translate,
      campaignService,
      groupService,
      userService,
      mentionService,
      modalService,
      validateErrorHandler,
      referenceDataService,
      eventTracker,
      cacheService
    );
  }

  ngOnInit() {
    this.sub = this.activatedRoute.params.subscribe(params => {
      this.campaignId = params['id'];
      this.tabKey = params['tab-key'] || CampaignTab.TERMS;
    });
    super.ngOnInit();
  }

  companyChangeCallback(companyId) {
    this.reloadData(this.campaignId, companyId);
  }

  // TODO a temporal solution to get the file size. We should move it to WS after saving file size in DB
  private calculateFileSize(url, materialImages) {
    const request = new XMLHttpRequest();
    request.open('GET', url, true);
    request.responseType = 'blob';
    request.onload = function() {
      const fileSize = request.response.size;
      const materialImage = materialImages.find(image => image.content === url);
      if (materialImage) {
        materialImage.fileSize = fileSize;
      }
    };
    request.send();
  }

  reloadData(campaignId: number, companyId: number) {
    // Get campaign details
    this.cacheService.get(`campaign_${campaignId}`, this.campaignService.getCampaign(campaignId, companyId)).subscribe(data => {
      this.isFetchingInfluencer = true;
      // process campaign data
      this.parseCampaignData(data.campaign, companyId);
      this.sendMixpanelEvent(this.campaign);
      this.isActiveCampaign = (this.campaignStatus && this.campaignStatus === CampaignStatus.ACTIVE) ? true : false;
      this.isArchivedCampaign = (this.campaignStatus && this.campaignStatus === CampaignStatus.ARCHIVED) ? true : false;
      // process term
      this.savedCampaignTerms = data.terms;
      this.fetchInfluencersOfCampaign(campaignId);
      this.selectableCampaignTerms = this.parseSelectableTerm(data.selectableTerms, data.terms, companyId);
      // process material files
      data.files.forEach(async (image) => {
        this.materialImages.push({
          id: image.id,
          name: image.fileName,
          contentType: image.contentType,
          campaignId: image.campaignId,
          action: 'none',
          content: image.fileUrl
        });
        this.calculateFileSize(image.fileUrl, this.materialImages);
      });
      // update Tab
      this.updateTabs();
    });
  }

  private fetchInfluencersOfCampaign(campaignId) {
    this.cacheService.get(`campaign_details_influencers_${campaignId}`,
      this.campaignService.getInfluencerByCampaign(campaignId)).subscribe(res => {
      // process influencers
      this.influencers = res.map(infl => this.campaignService.mapInfluencerCampaign(infl));
      this.campaignInfluencers = this.influencers;
      this.isFetchingInfluencer = false;
    });
  }

  private parseSelectableTerm(selectatbleTerms, terms, companyId) {
    selectatbleTerms.forEach(x => {
      const match = terms.find(a => a.termId === x.id);
      x.params = this.parseTerm(x);
      x.selected = !!match;
      x.companyId = companyId;

      // Update param value
      if (match && match.paramsAsJson && x.params) {
        for (const p in match.paramsAsJson) {
          if (!match.paramsAsJson.hasOwnProperty(p)) {
            continue;
          }

          const param = x.params.find(a => a.key === p);
          if (param) {
            param.value = match.paramsAsJson[p].value;
          }
        }
      }
    });
    return selectatbleTerms;
  }

  private parseCampaignData(campaignData, companyId) {
    // navigate to campaign list when opening a campaign which is NOT belonged to current user
    if (campaignData.companyId !== companyId) {
      this.router.navigate(['campaigns']);
      return;
    }
    this.campaign = campaignData;

    // Extract data
    this.campaignBrief = campaignData.brief;
    this.campaignAdditionalInfo = campaignData.additionalInfo;
    this.campaignName = campaignData.name;
    this.campaignStatus = campaignData.status;
    this.campaignCreatedAt = DateUtils.convertDateToString(new Date(campaignData.createdAt), 'dd-MM-yyyy');
    this.campaignArchivedAt = DateUtils.convertDateToString(new Date(campaignData.endDate), 'dd-MM-yyyy');

    // keywords
    this._mentions = campaignData.projectKeywords.map(x => x.keyword);
  }

  private sendMixpanelEvent(campaign) {
    this.eventTracker.trackUserAction(UserActionType.VIEW, Page.CAMPAIGN_SETTING,
      {
        id: campaign.id,
        name: campaign.name,
        type: TargetType.CAMPAIGN
      }, {
        source: ActionSource.PAGE,
        actionDetails: campaign.status === CampaignStatus.ARCHIVED
          ? ActivePages.ARCHIVED_CAMPAIGN : ActivePages.ACTIVE_CAMPAIGN
      }, undefined, undefined);
  }

  getEditTerms() {
    const savedTerms = this.savedCampaignTerms;
    const selectedTerms = this.selectedCampaignTerms;
    // edit terms
    const editTerms = selectedTerms
      .filter(x => savedTerms.some(a => a.termId === x.id))
      .map(x => {
        const id = savedTerms.find(a => a.termId === x.id).id;
        return {
          id: id,
          termId: x.id,
          action: TermActionType.EDIT,
          params: x.hasParams ? this.extractParams(x.params) : null
        };
      });

    const addTerms = selectedTerms
      .filter(x => !savedTerms.some(a => a.termId === x.id))
      .map(x => {
        return {
          termId: x.id,
          action: TermActionType.ADD,
          params: x.hasParams ? this.extractParams(x.params) : null
        };
      });

    const deleteTerms = savedTerms
      .filter(x => !selectedTerms.some(a => a.id === x.termId))
      .map(x => {
        return {
          id: x.id,
          termId: x.termId,
          action: TermActionType.DELETE,
          params: x.hasParams ? this.extractParams(x.params) : null
        };
      });

    return [...editTerms, ...addTerms, ...deleteTerms];
  }

  private getEditFiles() {
    return this.materialImages.map(x => {
      return {
        id: x.id,
        name: x.name,
        $$hashKey: '',
        fileId: x.id,
        contentType: x.contentType,
        action: x.action
      };
    });
  }

  replaceTabState(tabKey: string) {
    this.tabKey = tabKey;

    const params = {
      queryParams: {
        activeAccount: WebUtils.extractActiveAccountFromUrl()
      }
    };

    const url = this.router.createUrlTree(['/campaigns', this.campaignId, tabKey || CampaignTab.TERMS], params);
    this.location.replaceState(url.toString());
  }

  /**
   * handle update an existing campaign
   */
  public createOrUpdateCampaign() {
    this.isSavingCampaign = true;
    const campaignSaveData = {
      id: this.campaign.id,
      name: this.campaignName,
      projectType: 'CAMPAIGN_CREATION',
      brief: this.campaignBrief,
      additionalInfo: this.campaignAdditionalInfo,
      keywordIds: this._mentions.map(x => x.id),
      terms: this.getEditTerms(),
      influencerIds: this.campaignInfluencers.filter(influencer =>
        (influencer.campaign_influencer_status !== CampaignInfluencerStatus.NEW || !influencer.is_accepted))
        .map(x => x.influencer_id),
      company_id: this.campaign.company_id,
      needSendEmail: false,
      files: this.getEditFiles()
    };

    this.campaignService.updateCampaign(campaignSaveData)
      .subscribe(resp => {
        this.cacheService.clear(`campaign_${this.campaign.id}`);
        this.cacheService.clear(`campaign_details_influencers_${this.campaign.id}`);
        super.addNewAcceptedToCampaign(this.campaign.id, this.campaignInfluencers, () => { this.isSavingCampaign = false; });
        // track event to Segment
        this.eventTracker.trackUserAction(UserActionType.ACTION, Page.CAMPAIGN, {
          id: this.campaign.id,
          name: this.campaignName,
          type: TargetType.CAMPAIGN
        }, {
          action: UserActionType.UPDATE,
          actionDetails: ActionDetails.UPDATED_DETAILS,
          source: ActionSource.PAGE,
          ...campaignSaveData
        }, undefined, {
          type: ActivePages.CAMPAIGN_EDIT,
          name: this.campaignName,
          id: this.campaign.id
        });
        const newAddedInfluencers = this.campaignInfluencers.filter(c => c.campaign_influencer_status === 'new');
        if (newAddedInfluencers.length > 0) {
          super.sendEventWhenAddingInfluencersToCampaign(newAddedInfluencers);
        }
        this.userService.triggerUpdateActivatedInfluencers();
      }, (err) => {
        this.isSavingCampaign = false;
        if (err.error.data === 'invalid_level') {
          super.openAddInfluencerToCampaign();
          this.hasInvalidAccountLevelError = true;
          this.userService.doInvalidActionByLevel('Add to Campaign', err.error.message);
          super.sendInvalidActionToSegement(this.groupService.extractLimitInfoFromErrorMessage(err.error.message,
            this.campaignInfluencers.filter(c => c.campaign_influencer_status === 'new').length, this.userService.maxCampaignInfluencers, true), {
            campaign_id: this.campaign.id,
            campaign_name: this.campaignName,
            influencers: campaignSaveData.influencerIds
          });
        } else {
          return this.handleSaveError(err);
        }
      });
  }


  isCampaignDataChanged() {
    const hasChangeName = this.campaign && this.campaign.name !== this.campaignName;
    const hasChangeBrief = this.campaign && this.campaign.brief !== this.campaignBrief;
    const termList = this.getEditTerms();
    const hasChangeTermList = termList.filter(t => t.action === TermActionType.ADD || t.action === TermActionType.DELETE).length > 0;
    return this.isDirtyCampaignData || hasChangeName || hasChangeBrief || hasChangeTermList;
  }

  getCampaign() {
    const persistedCampaign: Campaign = new Campaign();
    persistedCampaign.id = this.campaignId;
    return persistedCampaign;
  }

  getCampaignCachingKeys() {
    if (this.campaign) {
      return [`campaign_${this.campaign.id}`, `campaign_details_influencers_${this.campaign.id}`];
    } else {
      return [];
    }
  }

  get campaginPopupSubtitle() {
    if (this.isActiveCampaign) {
      return this.translate.instant('campaigns.campaign_create_at', {createdAt: this.campaignCreatedAt});
    } else {
      return this.translate.instant('campaigns.campaign_archive_at', {archivedAt: this.campaignArchivedAt});
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/main/campaigns-add/campaign-edit.component.ts